import { Button } from "@/Button";
import { LayoutHeader } from "@/Header";
import { ValidateInput } from "@/Input";
import { SequentialAnimation } from "@/SequentialAnimation";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { Controller, useForm } from "react-hook-form";
import { useEffect, useMemo, useState } from "react";
import { ValidateUploader } from "@/Uploader";
import { ValidateSelectList } from "@/SelectList";
import { ValidateSelect } from "@/Select";
import { ValidateTextarea } from "@/Textarea";
import MessageModal from "@/MessageModal";
import useOpenModal from "^/common/useOpenModal";
import { useUserCreatePetForm, useUserPetUpdate } from "~/services/auth";
import {
  usePetInfo,
  usePetDeleteReason,
  useDeletePet,
} from "~/services/customer/myPage";
import { transformKeyValueArrayToOptions } from "%/dataTransform";
import { ValidateControllerSelect } from "@/ControllerSelect";
import { Dialog } from "@/Dialog";
import { useQueryClient } from "@tanstack/react-query";
import useHideNav from "^/common/useHideNav";

interface PetInfoChgParams {
  petSeq?: string;
}

export const Route = createFileRoute("/customer/layout/user-pet-info-chg")({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>): PetInfoChgParams => {
    return {
      petSeq: search.petSeq as string,
    };
  },
});

interface PetInfoForm {
  petImg: string | File;
  petName: string;
  petRegNumber: string;
  petType: string;
  gender: string;
  year: string;
  month: string;
  day: string;
  petWeight: string;
  neuterYn: string;
  inoculation: string;
  personality: string;
  allergy: string;
  memo: string;
  userName: string;
  userSeq: string;
  mainPetYn: string;
}

function RouteComponent() {
  const { petSeq } = Route.useSearch();
  const petSeqNumber = petSeq ? parseInt(petSeq, 10) : 0;
  const { data: petInfo, isLoading } = usePetInfo(petSeqNumber);

  // 로딩 중이거나 데이터가 없으면 로딩 표시
  if (isLoading || !petInfo) {
    return (
      <>
        <LayoutHeader
          title={"반려동물 정보 변경"}
          hasBackBtn
          hasFootPrint={false}
        />
        <div className="flex flex-auto justify-center items-center">
          <div>데이터 로딩 중...</div>
        </div>
      </>
    );
  }

  // 데이터가 완전히 로드된 후에만 실제 컴포넌트 렌더링
  return <PetInfoForm petInfo={petInfo} />;
}

// 별도 컴포넌트로 분리하여 데이터 로드 후에만 생성
function PetInfoForm({ petInfo }: { petInfo: any }) {
  useHideNav();
  const { history } = useRouter();
  const { data: petFormData } = useUserCreatePetForm();
  const { mutate: updatePet } = useUserPetUpdate();
  const queryClient = useQueryClient();
  const { data: deleteReasons } = usePetDeleteReason();
  const { mutate: deletePet } = useDeletePet();
  console.log(deleteReasons);
  // 모달 상태 관리
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalMessage, setModalMessage] = useState({
    title: "알림",
    message1: "",
    message2: "",
    message3: "",
  });
  const [showValidationErrorDialog, setShowValidationErrorDialog] =
    useState(false);
  const [validationErrorMessage, setValidationErrorMessage] = useState("");

  // 삭제 성공 Dialog 상태 관리
  const [isDeleteSuccessDialogVisible, setIsDeleteSuccessDialogVisible] =
    useState(false);

  const showModal = (
    message1: string,
    message2: string = "",
    message3: string = "",
    title: string = "알림"
  ) => {
    setModalMessage({ title, message1, message2, message3 });
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    history.back();
  };

  // 삭제 성공 Dialog 핸들러
  const handleDeleteSuccessDialogClose = () => {
    setIsDeleteSuccessDialogVisible(false);
    history.back(); // 이전 페이지로 이동
  };

  // 생년월일 파싱
  const petBirthday = petInfo.petBirthday || "";
  const birthYear = petBirthday.substring(0, 4);
  const birthMonth = petBirthday.substring(4, 6);
  const birthDay = petBirthday.substring(6, 8);

  // 폼 기본값 생성
  console.log("=== petInfo 확인 ===");
  console.log("petInfo 존재 여부:", !!petInfo);
  console.log("petInfo:", petInfo);

  const formDefaultValues = petInfo
    ? (() => {
        console.log("=== formDefaultValues 생성 시작 ===");
        console.log("petInfo:", petInfo);
        console.log("petInfo.inoculations:", petInfo.inoculations);
        console.log("petInfo.personalities:", petInfo.personalities);

        // 접종 정보에서 activeYn이 Y인 값들 추출
        const activeInoculations = Array.isArray(petInfo.inoculations)
          ? petInfo.inoculations
              .filter((item: any) => item.activeYn === "Y")
              .map((item: any) => item.cdInoculation)
          : petInfo.inoculations
            ? JSON.parse(petInfo.inoculations)
                .filter((item: any) => item.activeYn === "Y")
                .map((item: any) => item.cdInoculation)
            : [];

        console.log("activeInoculations:", activeInoculations);

        // 성격/성향에서 activeYn이 Y인 값들 추출
        const activePersonalities = Array.isArray(petInfo.personalities)
          ? petInfo.personalities
              .filter((item: any) => item.activeYn === "Y")
              .map((item: any) => item.cdPersonality)
          : petInfo.personalities
            ? JSON.parse(petInfo.personalities)
                .filter((item: any) => item.activeYn === "Y")
                .map((item: any) => item.cdPersonality)
            : [];

        console.log("activePersonalities:", activePersonalities);

        const defaultValues = {
          petImg: petInfo.fileUrl || "",
          petName: petInfo.petName || "",
          petRegNumber: petInfo.petRegNumber || "",
          petType: petInfo.cdPetBreed || "",
          gender: petInfo.cdPetGender || "",
          year: birthYear || "",
          month: birthMonth || "",
          day: birthDay || "",
          petWeight: petInfo.petWeight?.toString() || "",
          neuterYn: petInfo.neuterYn || "",
          inoculation: activeInoculations.join(","),
          personality: activePersonalities.join(","),
          allergy: petInfo.allergy || "",
          memo: petInfo.memo || "",
          userName: petInfo.userName || "",
          userSeq: petInfo.userSeq?.toString() || "",
          mainPetYn: petInfo.mainPetYn || "",
        };

        console.log("생성된 defaultValues:", defaultValues);
        console.log("defaultValues.inoculation:", defaultValues.inoculation);
        console.log("defaultValues.personality:", defaultValues.personality);
        console.log("=== formDefaultValues 생성 완료 ===");
        return defaultValues;
      })()
    : (() => {
        console.log("=== petInfo가 없어서 빈 값으로 설정 ===");
        return {
          petImg: "",
          petName: "",
          petRegNumber: "",
          petType: "",
          gender: "",
          year: "",
          month: "",
          day: "",
          petWeight: "",
          neuterYn: "",
          inoculation: "",
          personality: "",
          allergy: "",
          memo: "",
          userName: "",
          userSeq: "",
          mainPetYn: "",
        };
      })();

  console.log("=== formDefaultValues 최종 완료 ===");
  console.log("formDefaultValues:", formDefaultValues);

  const {
    handleSubmit,
    control,
    setValue,
    formState: { isValid, errors },
    watch,
    register,
  } = useForm<PetInfoForm>({
    defaultValues: formDefaultValues,
    mode: "onChange",
  });

  // petInfo가 변경될 때마다 폼 값을 강제로 업데이트
  useEffect(() => {
    if (petInfo) {
      console.log("=== 폼 값 강제 업데이트 시작 ===");
      console.log("petInfo:", petInfo);

      // 생년월일 파싱
      const petBirthday = petInfo.petBirthday || "";
      const birthYear = petBirthday.substring(0, 4);
      const birthMonth = petBirthday.substring(4, 6);
      const birthDay = petBirthday.substring(6, 8);

      // 접종 정보에서 activeYn이 Y인 값들 추출
      const activeInoculations = Array.isArray(petInfo.inoculations)
        ? petInfo.inoculations
            .filter((item: any) => item.activeYn === "Y")
            .map((item: any) => item.cdInoculation)
        : petInfo.inoculations
          ? JSON.parse(petInfo.inoculations)
              .filter((item: any) => item.activeYn === "Y")
              .map((item: any) => item.cdInoculation)
          : [];

      // 성격/성향에서 activeYn이 Y인 값들 추출
      const activePersonalities = Array.isArray(petInfo.personalities)
        ? petInfo.personalities
            .filter((item: any) => item.activeYn === "Y")
            .map((item: any) => item.cdPersonality)
        : petInfo.personalities
          ? JSON.parse(petInfo.personalities)
              .filter((item: any) => item.activeYn === "Y")
              .map((item: any) => item.cdPersonality)
          : [];

      // 폼 값들을 강제로 업데이트
      setValue("petImg", petInfo.fileUrl || "");
      setValue("petName", petInfo.petName || "");
      setValue("petRegNumber", petInfo.petRegNumber || "");
      setValue("petType", petInfo.cdPetBreed || "");
      setValue("gender", petInfo.cdPetGender || "");
      setValue("year", birthYear || "");
      setValue("month", birthMonth || "");
      setValue("day", birthDay || "");
      setValue("petWeight", petInfo.petWeight?.toString() || "");
      setValue("neuterYn", petInfo.neuterYn || "");
      setValue("inoculation", activeInoculations.join(","));
      setValue("personality", activePersonalities.join(","));
      setValue("allergy", petInfo.allergy || "");
      setValue("memo", petInfo.memo || "");
      setValue("userName", petInfo.userName || "");
      setValue("userSeq", petInfo.userSeq?.toString() || "");
      setValue("mainPetYn", petInfo.mainPetYn || "");

      console.log("=== 폼 값 강제 업데이트 완료 ===");
    }
  }, [petInfo, setValue]);

  console.log("=== useForm 생성 완료 ===");
  console.log("formDefaultValues:", formDefaultValues);

  const bindSubmit = async (data: PetInfoForm) => {
    console.log("Pet update form data:", data);
    console.log("data.petImg:", data.petImg);
    console.log("data.petImg type:", typeof data.petImg);
    console.log("data.petImg instanceof File:", data.petImg instanceof File);
    console.log(petFormData);
    console.log(petInfo);

    // 이미지 변경 여부 확인
    const isImageChanged = data.petImg !== petInfo.fileUrl;
    console.log("이미지 변경 여부:", isImageChanged);
    console.log("현재 이미지:", data.petImg);
    console.log("원본 이미지:", petInfo.fileUrl);

    // 접종 정보 변환 (pet.tsx와 동일한 패턴)
    const inoculation = petFormData?.inoculations.map((item) => {
      if (data.inoculation.includes(item.cdInoculation)) {
        return {
          ...item,
          activeYn: "Y",
        };
      } else {
        return {
          ...item,
          activeYn: "N",
        };
      }
    });

    // 성격 정보 변환 (pet.tsx와 동일한 패턴)
    const personality = petFormData?.personalities.map((item) => {
      if (data.personality.includes(item.cdPersonality)) {
        return {
          ...item,
          activeYn: "Y",
        };
      } else {
        return {
          ...item,
          activeYn: "N",
        };
      }
    });

    console.log("변환된 접종 정보:", inoculation);
    console.log("변환된 성격 정보:", personality);

    // API 형태에 맞게 데이터 변환
    const petUpdateData = {
      ...data,
      petSeq: petInfo.petSeq,
      userName: petInfo.userName,
      inoculations: inoculation,
      personalities: personality,
      userSeq: petInfo.userSeq,
      // 이미지가 변경된 경우에만 새로운 이미지 전송
      petImg: isImageChanged ? data.petImg : "",
    };

    updatePet(petUpdateData, {
      onSuccess: (response) => {
        console.log("펫 업데이트 성공:", response);
        if (response.statusCode === 200) {
          // 관련 쿼리들 무효화하여 최신 데이터로 갱신
          queryClient.invalidateQueries({
            queryKey: ["signIn", "user", "createPetForm"],
          });
          queryClient.invalidateQueries({ queryKey: ["mypage", "petInfo"] });

          showModal(
            "반려동물 정보가 성공적으로 수정되었습니다.",
            "",
            "",
            "저장 완료"
          );
        }
      },
      onError: (error) => {
        console.error("펫 업데이트 실패:", error);
        showModal(
          "반려동물 정보 수정 중 오류가 발생했습니다.",
          "다시 시도해주세요.",
          "",
          "오류"
        );
      },
    });
  };

  // 연도 선택 옵션 생성 (현재 연도부터 20년 전까지)
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 20 }, (_, i) => currentYear - i);
  }, []);

  // 월 선택 옵션 생성 (1-12월)
  const monthOptions = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => i + 1);
  }, []);

  // 일 선택 옵션 생성 (1-31일)
  const dayOptions = useMemo(() => {
    const selectedYear = parseInt(watch("year"));
    const selectedMonth = parseInt(watch("month"));

    if (!selectedYear || !selectedMonth)
      return Array.from({ length: 31 }, (_, i) => i + 1);

    const lastDay = new Date(selectedYear, selectedMonth, 0).getDate();
    return Array.from({ length: lastDay }, (_, i) => i + 1);
  }, [watch("year"), watch("month")]);

  // 모달
  const [deleteReason, setDeleteReason] = useState<string>("");
  const [etcReason, setEtcReason] = useState("");
  const { isOpenModal, clickModal, closeModal } = useOpenModal();
  const {
    isOpenModal: isOpenSecondModal,
    // clickModal: clickSecondModal,
    closeModal: closeSecondModal,
  } = useOpenModal();

  const handleDelete = () => {
    clickModal();
  };
  useEffect(() => {
    console.log(deleteReason);
  }, [deleteReason]);
  const handleConfirm = () => {
    try {
      // 선택된 삭제 사유에서 해당 object 찾기
      const selectedReason = deleteReasons?.find(
        (reason: any) => reason.cdPetDelete === deleteReason
      );
      console.log(selectedReason);
      if (!selectedReason) {
        alert("삭제 사유를 선택해주세요.");
        return;
      }
      // API 호출
      deletePet(
        {
          petSeq: petInfo.petSeq,
          responseList: [
            {
              activeYn: "Y",
              reasonName: selectedReason.reasonName,
              cdPetDelete: selectedReason.cdPetDelete,
              etcContent:
                deleteReason === "PET_DELETE_TYPE_OTHER" ? etcReason : null,
            },
          ],
        },
        {
          onSuccess: () => {
            setIsDeleteSuccessDialogVisible(true);
          },
          onError: (error) => {
            console.error("펫 삭제 실패:", error);
            alert("펫 삭제에 실패했습니다.");
          },
        }
      );
    } catch (error) {
      console.error("펫 삭제 처리 중 오류:", error);
      alert("펫 삭제 처리 중 오류가 발생했습니다.");
    }

    closeModal();
  };

  // API 데이터를 옵션 형태로 변환
  const petBreedOptions = useMemo(() => {
    if (!petFormData?.petBreeds) {
      console.log("petBreeds가 없어서 빈 배열 반환");
      return [];
    }
    const options = transformKeyValueArrayToOptions(petFormData.petBreeds);
    return options;
  }, [petFormData?.petBreeds]);

  const personalityOptions = useMemo(() => {
    if (!petFormData?.personalities) return [];
    return petFormData.personalities.map((item) => ({
      label: item.personalityName,
      value: item.cdPersonality,
    }));
  }, [petFormData?.personalities]);

  const inoculationOptions = useMemo(() => {
    if (!petFormData?.inoculations) return [];
    return petFormData.inoculations.map((item) => ({
      label: item.inoculationName,
      value: item.cdInoculation,
    }));
  }, [petFormData?.inoculations]);

  return (
    <>
      <LayoutHeader title={"반려동물 정보 변경"} hasBackBtn hasFootPrint={false} />
      {/* 수신자 */}
      <div className="flex flex-col flex-auto bg-[#F5F5F5]">
        <div className="flex justify-between px-5 pt-3">
          <div className="text-[16px] font-semibold">
            {petInfo.petName} 등록정보 수정
          </div>
          <button onClick={() => handleDelete()}>
            <span className="text-[14px] text-[#666666]">삭제</span>
          </button>
        </div>
        <form
          onSubmit={handleSubmit(bindSubmit)}
          className="flex flex-col flex-auto gap-4 px-5 pt-2"
        >
          <SequentialAnimation>
            <ValidateInput
              className="w-full"
              label="보호자 이름"
              disabled
              {...register("userName")}
            />
            <Controller
              name="petImg"
              control={control}
              rules={{ required: "반려동물 사진을 업로드하세요" }}
              render={({ field }) => (
                <ValidateUploader
                  onUploaded={(image) => {
                    // 이미지가 삭제된 경우 빈 문자열로 설정, 업로드된 경우 해당 이미지로 설정
                    field.onChange(image || "");
                  }}
                  onValidationError={(error) => {
                    setValidationErrorMessage(error);
                    setShowValidationErrorDialog(true);
                  }}
                  errorMessage={errors?.petImg?.message}
                  className="w-full"
                  label="반려동물 사진 등록"
                  defaultValue={
                    typeof field.value === "string" && field.value
                      ? field.value
                      : undefined
                  }
                  maxSizeMB={10}
                />
              )}
            />
            <Controller
              name="petName"
              control={control}
              rules={{ required: "반려동물 이름을 입력하세요" }}
              render={({ field }) => (
                <ValidateInput
                  {...field}
                  className="w-full"
                  label="반려동물 이름"
                  errorMessage={errors?.petName?.message}
                />
              )}
            />
            <Controller
              name="petRegNumber"
              control={control}
              render={({ field }) => (
                <ValidateInput
                  {...field}
                  className="w-full"
                  label="국가동물등록번호(선택)"
                />
              )}
            />
            <Controller
               name="petType"
               control={control}
               rules={{ required: "반려동물 품종을 선택하세요" }}
               render={({ field }) => {

                const matchingOption = field.value && petBreedOptions.length > 0
                     ? petBreedOptions.find(
                         (option) => option.value === String(field.value)
                       )
                     : null;
                 return (
                   <ValidateSelectList
                     onChange={(selectedOption) => {
                       console.log("ValidateSelectList onChange:", selectedOption);
                       field.onChange(selectedOption?.value || "");
                     }}
                     label="반려동물 품종"
                     placeholder="품종을 선택하세요"
                     required={true}
                     isSearchable={true}
                     errorMessage={errors?.petType?.message}
                     options={petBreedOptions}
                     defaultValues={matchingOption || undefined}
                     value={matchingOption}
                   />
                 );
               }}
             />
            <Controller
              name="gender"
              control={control}
              rules={{ required: "성별을 선택하세요" }}
              render={({ field }) => (
                <ValidateControllerSelect
                  value={field.value ? field.value.split(",") : []}
                  onChange={(value) => {
                    field.onChange(value.join(","));
                  }}
                  label="성별"
                  required={true}
                  max={1}
                  col={2}
                  errorMessage={errors?.gender?.message}
                  options={[
                    { label: "암컷", value: "FEMALE" },
                    { label: "수컷", value: "MALE" },
                  ]}
                />
              )}
            />
            <div>
              <label className="block mb-2 font-medium text-gray-900">
                반려동물 나이 <span className="text-orange-500">*</span>
              </label>
              <div className="flex gap-2 w-full">
                <Controller
                  name="year"
                  control={control}
                  rules={{ required: "연도를 입력하세요" }}
                  render={({ field }) => {
                    console.log("year field.value:", field.value);

                    const yearOptionsWithLabels = yearOptions.map((year) => ({
                      value: String(year),
                      label: `${year}년`,
                    }));

                    return (
                      <ValidateSelectList
                        onChange={(selectedOption) =>
                          field.onChange(selectedOption?.value || "")
                        }
                        value={
                          field.value
                            ? yearOptionsWithLabels.find(
                                (option) => option.value === field.value
                              ) || null
                            : null
                        }
                        placeholder="년도"
                        isSearchable={false}
                        options={yearOptionsWithLabels}
                        className="h-14 text-lg text-center"
                      />
                    );
                  }}
                />
                <Controller
                  name="month"
                  control={control}
                  rules={{ required: "월을 입력하세요" }}
                  render={({ field }) => {
                    console.log("month field.value:", field.value);

                    const monthOptionsWithLabels = monthOptions.map(
                      (month) => ({
                        value: month.toString().padStart(2, "0"),
                        label: `${month}월`,
                      })
                    );

                    return (
                      <ValidateSelectList
                        onChange={(selectedOption) =>
                          field.onChange(selectedOption?.value || "")
                        }
                        value={
                          field.value
                            ? monthOptionsWithLabels.find(
                                (option) => option.value === field.value
                              ) || null
                            : null
                        }
                        placeholder="00월"
                        isSearchable={false}
                        options={monthOptionsWithLabels}
                        className="h-14 text-lg text-center"
                      />
                    );
                  }}
                />
                <Controller
                  name="day"
                  control={control}
                  rules={{ required: "일을 입력하세요" }}
                  render={({ field }) => {
                    console.log("day field.value:", field.value);

                    const dayOptionsWithLabels = dayOptions.map((day) => ({
                      value: String(day),
                      label: `${day.toString().padStart(2, "0")}일`,
                    }));

                    return (
                      <ValidateSelectList
                        onChange={(selectedOption) =>
                          field.onChange(selectedOption?.value || "")
                        }
                        value={
                          field.value
                            ? dayOptionsWithLabels.find(
                                (option) =>
                                  option.value === field.value ||
                                  option.value === String(parseInt(field.value))
                              ) || null
                            : null
                        }
                        placeholder="00일"
                        isSearchable={false}
                        options={dayOptionsWithLabels}
                        className="h-14 text-lg text-center"
                      />
                    );
                  }}
                />
              </div>
            </div>

            <div className="relative pr-6 w-fit">
              <Controller
                name="petWeight"
                control={control}
                rules={{ required: "반려동물 몸무게를 입력하세요" }}
                render={({ field }) => (
                  <ValidateInput
                    {...field}
                    type="number"
                    inputMode="decimal"
                    label="반려동물 몸무게"
                    required={true}
                    errorMessage={errors?.petWeight?.message}
                  />
                )}
              />
              <span className="absolute right-0 bottom-0">kg</span>
            </div>
            <Controller
              name="neuterYn"
              control={control}
              rules={{ required: "중성화 여부를 선택하세요" }}
              render={({ field }) => (
                <ValidateControllerSelect
                  value={field.value ? field.value.split(",") : []}
                  onChange={(value) => {
                    field.onChange(value.join(","));
                  }}
                  label="중성화"
                  required={true}
                  col={2}
                  max={1}
                  errorMessage={errors?.neuterYn?.message}
                  options={[
                    { label: "중성화 완료", value: "Y" },
                    { label: "중성화 예정/미완료", value: "N" },
                  ]}
                />
              )}
            />
            <Controller
              name="inoculation"
              control={control}
              rules={{ required: "접종 정보를 선택하세요" }}
              render={({ field }) => {
                return (
                  <ValidateControllerSelect
                    value={
                      field.value && field.value !== ""
                        ? field.value.split(",")
                        : []
                    }
                    onChange={(value) => {
                      field.onChange(value.join(","));
                    }}
                    label="최근 1년 이내 접종 정보"
                    required={true}
                    errorMessage={errors?.inoculation?.message}
                    options={inoculationOptions}
                  />
                );
              }}
            />
            <Controller
              name="personality"
              control={control}
              rules={{ required: "성격/성향을 선택하세요" }}
              render={({ field }) => {
                return (
                  <ValidateControllerSelect
                    value={
                      field.value && field.value !== ""
                        ? field.value.split(",")
                        : []
                    }
                    onChange={(value) => {
                      console.log("personality onChange:", value);
                      field.onChange(value.join(","));
                    }}
                    label="해당하는 성격/성향을 모두 선택해주세요"
                    required={true}
                    errorMessage={errors?.personality?.message}
                    options={personalityOptions}
                  />
                );
              }}
            />
            <Controller
              name="allergy"
              control={control}
              render={({ field }) => (
                <ValidateTextarea
                  {...field}
                  label="알레르기 정보를 알려주세요"
                />
              )}
            />
            <Controller
              name="memo"
              control={control}
              render={({ field }) => (
                <ValidateTextarea {...field} label="기타 메모" />
              )}
            />
          </SequentialAnimation>
        </form>
        <div className="px-4 py-6 w-full bg-[#F5F5F5]">
          <Button
            className="w-full bg-[#F5F5F5]"
            style={{ pointerEvents: "auto" }}
            onClick={handleSubmit(bindSubmit)}
            disabled={!isValid}
          >
            <div className="py-3 mx-auto w-full text-base font-semibold text-center text-white bg-orange-400 rounded-full">
              반려동물 정보 저장하기
            </div>
          </Button>
        </div>
        <MessageModal
          isOpen={isOpenModal}
          onRequestClose={closeModal}
          className="pt-0 pb-6 mx-auto w-full max-w-sm bg-white rounded-2xl border-2 border-gray-200 shadow-lg outline-none"
          overlayClassName="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
          shouldCloseOnOverlayClick={false}
        >
          <div className="flex flex-col gap-3">
            <div className="text-lg font-semibold text-center">
              반려동물 정보 삭제
            </div>
            <div className="text-sm text-center">삭제 사유를 알려주세요</div>
          </div>
          <form>
            <div className="my-5">
              <ValidateSelect
                max={1}
                defaultValues={[deleteReason || ""]}
                className="rounded-full"
                col={4}
                onChange={(val) => setDeleteReason(val[0] || "")}
                options={
                  deleteReasons?.map((item: any) => ({
                    label: item.reasonName,
                    value: item.cdPetDelete,
                  })) || []
                }
              />
              {deleteReason === "PET_DELETE_TYPE_OTHER" && (
                <input
                  className="px-3 py-2 mt-3 w-full rounded-lg border border-orange-400 focus:outline-none"
                  placeholder="사유를 입력해주세요"
                  value={etcReason}
                  onChange={(e) => setEtcReason(e.target.value)}
                />
              )}
            </div>
          </form>
          <div className="flex gap-3 mt-2">
            <button
              className="flex-1 py-2 font-semibold text-gray-700 bg-gray-200 rounded-full border border-gray-300"
              onClick={closeModal}
            >
              취소
            </button>
            <button
              className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full"
              onClick={handleConfirm}
            >
              확인
            </button>
          </div>
        </MessageModal>

        <MessageModal
          isOpen={isOpenSecondModal}
          onRequestClose={closeSecondModal}
          className="pt-0 pb-6 mx-auto w-full max-w-sm bg-white rounded-2xl border-2 border-gray-200 shadow-lg outline-none"
          overlayClassName="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
          shouldCloseOnOverlayClick={false}
        >
          <div className="flex flex-col gap-3">
            <div className="text-lg font-semibold text-center">
              반려동물 정보 삭제
            </div>
            <div className="text-sm text-center">삭제되었습니다.</div>
          </div>
          <div className="flex gap-3 justify-center mt-2">
            <button
              className="py-2 w-3/5 font-semibold text-white bg-orange-400 rounded-full"
              onClick={closeSecondModal}
            >
              확인
            </button>
          </div>
        </MessageModal>
      </div>

      {/* 저장 결과 메시지 모달 */}
      <Dialog
        visible={isModalVisible}
        onClose={handleModalClose}
        persist={false}
      >
        <div className="flex flex-col gap-1">
          <div className="flex flex-col mb-2">
            <div className="text-lg font-semibold text-center">
              {modalMessage.title}
            </div>
          </div>
          <div className="flex flex-col mb-2">
            <div className="text-sm text-center text-[#666666]">
              {modalMessage.message1}
            </div>
            {modalMessage.message2 && (
              <div className="text-sm text-center text-[#666666]">
                {modalMessage.message2}
              </div>
            )}
            {modalMessage.message3 && (
              <div className="text-sm text-center text-[#666666]">
                {modalMessage.message3}
              </div>
            )}
          </div>
          <div className="flex gap-3 mt-2">
            <button
              className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full"
              onClick={handleModalClose}
            >
              확인
            </button>
          </div>
        </div>
      </Dialog>

      {/* 삭제 성공 Dialog */}
      <Dialog
        visible={isDeleteSuccessDialogVisible}
        onClose={handleDeleteSuccessDialogClose}
        persist={false}
      >
        <div className="flex flex-col gap-1">
          <div className="flex flex-col mb-2">
            <div className="text-lg font-semibold text-center">삭제 완료</div>
          </div>
          <div className="flex flex-col mb-2">
            <div className="text-sm text-center text-[#666666]">
              반려동물이 성공적으로 삭제되었습니다.
            </div>
          </div>
          <div className="flex gap-3 mt-2">
            <button
              className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full"
              onClick={handleDeleteSuccessDialogClose}
            >
              확인
            </button>
          </div>
        </div>
      </Dialog>

      {/* 파일 검증 에러 Dialog */}
      <Dialog
        visible={showValidationErrorDialog}
        onClose={() => setShowValidationErrorDialog(false)}
      >
        <div className="text-center">
          <h3 className="mb-2 text-lg font-semibold">파일 형식 오류</h3>
          <p className="mb-4 text-gray-600">{validationErrorMessage}</p>
          <p className="mb-4 text-sm text-gray-500">
            이미지 파일만 업로드 가능합니다. (JPG, PNG, GIF, WEBP, BMP, SVG)
          </p>
          <button
            onClick={() => setShowValidationErrorDialog(false)}
            className="px-4 py-2 text-white bg-orange-500 rounded-lg transition-colors hover:bg-orange-600"
          >
            확인
          </button>
        </div>
      </Dialog>
    </>
  );
}
